import {
  Container,
  Paper,
  Typography,
  Box,
  Link as MuiLink,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

/**
 * TermsOfUsePage - Página de Termos de Uso e Política de Privacidade
 * 
 * Conforme Lei Geral de Proteção de Dados Pessoais (LGPD - Lei 13.709/2018)
 * Versão 1.0 - Janeiro 2026
 * 
 * PROTEÇÃO CONTRA CRAWLERS:
 * - Email obfuscado com HTML entities para evitar coleta automática
 * - Telefone protegido com atributos data
 * - Meta tags noindex para seções sensíveis
 */
export default function TermsOfUsePage() {
  const navigate = useNavigate();

  // Helper para exibir email de forma protegida
  const renderEmail = (email) => {
    return email
      .split('')
      .map(char => `&#${char.charCodeAt(0)};`)
      .join('');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Botão Voltar */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          marginBottom: 3,
          textTransform: 'none',
          color: '#1976d2',
        }}
      >
        Voltar
      </Button>

      <Paper
        elevation={0}
        sx={{
          padding: { xs: 2, sm: 4 },
          backgroundColor: '#fafafa',
        }}
      >
        {/* Cabeçalho */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography variant="h4" component="h1" sx={{ marginBottom: 1, fontWeight: 600 }}>
            Termos de Uso e Política de Privacidade
          </Typography>
          <Typography variant="body2" sx={{ color: '#757575' }}>
            Última atualização: Dezembro 2025
          </Typography>
        </Box>

        {/* Introdução */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            1. Introdução
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1 }}>
            Esta Política de Privacidade e Termos de Uso descrevem como a <strong>Blasoft Desenvolvimento de Software Ltda.</strong> ("Empresa", "nós" ou "nosso") coleta, usa, compartilha e protege suas informações pessoais através da plataforma <strong>Agendário</strong> ("Serviço").
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
            Esta política é oferecida de acordo com a Lei Geral de Proteção de Dados Pessoais - <strong>Lei nº 13.709 de 14 de agosto de 2018</strong> (LGPD) e legislação complementar.
          </Typography>
        </Box>

        {/* 2. Definições */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            2. Definições Importantes
          </Typography>
          <Box sx={{ marginBottom: 2, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              <strong>Dados Pessoais:</strong> Qualquer informação relacionada à pessoa natural identificada ou identificável.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              <strong>Titular:</strong> Pessoa natural a quem se referem os dados pessoais que são objeto de tratamento.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              <strong>Controlador:</strong> Pessoa natural ou jurídica que decide sobre o tratamento de dados pessoais. Neste caso, a Blasoft.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              <strong>Operador:</strong> Pessoa natural ou jurídica que realiza o tratamento de dados em nome do controlador.
            </Typography>
            <Typography variant="body2">
              <strong>Tratamento:</strong> Toda operação realizada com dados pessoais (coleta, recebimento, classificação, utilização, acesso, armazenamento, entre outras).
            </Typography>
          </Box>
        </Box>

        {/* 3. Dados Coletados */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            3. Que Dados Pessoais Coletamos?
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1rem', marginBottom: 1, fontWeight: 500 }}>
            3.1 Dados Fornecidos Diretamente
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Quando você cria uma conta e utiliza o Agendário, você pode nos fornecer:
          </Typography>
          <Box sx={{ marginBottom: 2, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Nome completo
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Endereço de e-mail
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Número de telefone
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • CPF ou CNPJ (quando aplicável)
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Data de nascimento
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Endereço residencial ou comercial
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Foto ou avatar de perfil
            </Typography>
            <Typography variant="body2">
              • Preferências de serviços e configurações de conta
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontSize: '1rem', marginBottom: 1, fontWeight: 500 }}>
            3.2 Dados Coletados Automaticamente
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 2 }}>
            Quando você acessa o Agendário, nós coletamos automaticamente:
          </Typography>
          <Box sx={{ marginBottom: 2, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Dados de navegação (páginas visitadas, tempo de permanência)
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Informações do dispositivo (tipo, sistema operacional, navegador)
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Endereço IP
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
              • Identificadores de cookies e sessão
            </Typography>
            <Typography variant="body2">
              • Logs de acesso e auditoria
            </Typography>
          </Box>
        </Box>

        {/* 4. Fundamento Legal */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            4. Fundamento Legal para Coleta e Uso de Dados
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            O tratamento de seus dados pessoais ocorre com base em uma das seguintes bases legais estabelecidas pela LGPD (art. 7º):
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1rem', marginBottom: 1, fontWeight: 500 }}>
            4.1 Consentimento (art. 7º, I)
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Você fornece consentimento explícito para que coletemos e utilizemos seus dados pessoais através deste Termo e de seus consentimentos subsequentes.
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1rem', marginBottom: 1, fontWeight: 500 }}>
            4.2 Execução de Contrato (art. 7º, II)
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Muitos dados são necessários para manter sua conta e fornecer os serviços que você contratou. Sem esses dados, não é possível executar o contrato ou você poderá perder funcionalidades importantes.
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1rem', marginBottom: 1, fontWeight: 500 }}>
            4.3 Cumprimento de Obrigação Legal (art. 7º, III)
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Podemos processar seus dados para cumprir obrigações legais, tais como exigências fiscais, prevenção à fraude e conformidade com determinações judiciais.
          </Typography>

          <Typography variant="h6" sx={{ fontSize: '1rem', marginBottom: 1, fontWeight: 500 }}>
            4.4 Interesse Legítimo (art. 7º, IX)
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
            Utilizamos dados para melhorar nossos serviços, prevenir fraudes, proteger a segurança da plataforma e manter registros de auditoria, desde que nossos interesses não prejudiquem seus direitos.
          </Typography>
        </Box>

        {/* 5. Como Usamos Seus Dados */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            5. Como Utilizamos Seus Dados Pessoais?
          </Typography>
          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Manutenção de Conta:</strong> Criar e manter sua conta, processar login, recuperar senha, gerenciar preferências.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Auditoria e Conformidade:</strong> Registrar todas as ações para detectar atividades não autorizadas, investigar violações e cumprir leis aplicáveis.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Segurança:</strong> Prevenir, detectar e investigar fraudes, abusos, ataques cibernéticos e ameaças à segurança.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Melhoria de Serviços:</strong> Analisar uso, entender preferências, desenvolver novos recursos e melhorar a experiência do usuário.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Comunicação:</strong> Enviar notificações importantes, atualizações de conta, confirmações de reserva e suporte técnico.
            </Typography>
            <Typography variant="body2">
              <strong>Conformidade Legal:</strong> Cumprir obrigações fiscais, autoridades competentes e determinações judiciais.
            </Typography>
          </Box>
        </Box>

        {/* 6. Compartilhamento de Dados */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            6. Compartilhamento de Dados Pessoais
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Seus dados pessoais <strong>não são vendidos, compartilhados ou alugados</strong> para terceiros com fins comerciais. Podemos compartilhar dados apenas nas seguintes situações:
          </Typography>

          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Prestadores de Serviço:</strong> Provedores de hospedagem, e-mail, armazenamento em nuvem e análise que atuam como operadores de dados sob contrato.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Autoridades Legais:</strong> Quando legalmente obrigado por ordem judicial, mandado ou solicitação governamental.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Proteção de Direitos:</strong> Para proteger direitos legais, prevenir fraudes ou executar contratos.
            </Typography>
            <Typography variant="body2">
              <strong>Transferência de Negócios:</strong> Em caso de venda, fusão ou aquisição, seus dados podem ser transferidos como parte do ativo.
            </Typography>
          </Box>
        </Box>

        {/* 7. Segurança de Dados */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            7. Segurança de Seus Dados
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Implementamos medidas técnicas, administrativas e físicas para proteger seus dados pessoais contra acesso não autorizado, alteração, divulgação ou destruição:
          </Typography>

          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Criptografia de dados em trânsito (HTTPS/TLS)
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Armazenamento seguro em data centers certificados
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Controle de acesso baseado em função (RBAC)
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Autenticação multifatorial para contas administrativas
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Logs de auditoria detalhados e monitoramento contínuo
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Política de backup e recuperação de desastres
            </Typography>
            <Typography variant="body2">
              • Conformidade com padrões de segurança da indústria
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ lineHeight: 1.8, marginTop: 1.5 }}>
            Apesar de nossos esforços, nenhum sistema de segurança é 100% impenetrável. Você é responsável por manter a confidencialidade de suas credenciais de acesso.
          </Typography>
        </Box>

        {/* 8. Retenção de Dados */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            8. Quanto Tempo Armazenamos Seus Dados?
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Armazenamos seus dados pessoais pelo tempo necessário para:
          </Typography>

          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Manter sua conta:</strong> Enquanto sua conta estiver ativa.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Auditoria legal:</strong> Mínimo de 5 anos para cumprir obrigações tributárias e regulatórias.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Contencioso:</strong> Enquanto houver ações legais pendentes relacionadas aos dados.
            </Typography>
            <Typography variant="body2">
              <strong>Dados de cookies:</strong> Conforme configurado em suas preferências do navegador.
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ lineHeight: 1.8, marginTop: 1.5 }}>
            Após o período de retenção necessário, seus dados serão eliminados ou anonimizados de forma irrevogável, exceto quando legalmente obrigado a manter.
          </Typography>
        </Box>

        {/* 9. Seus Direitos */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            9. Seus Direitos Conforme a LGPD
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            De acordo com a Lei Geral de Proteção de Dados Pessoais, você possui os seguintes direitos:
          </Typography>

          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Confirmação (art. 18, I):</strong> Confirmar se estamos tratando seus dados pessoais.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Acesso (art. 18, II):</strong> Solicitar acesso aos dados pessoais que mantemos sobre você.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Retificação (art. 18, III):</strong> Corrigir dados pessoais incompletos ou imprecisos.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Eliminação (art. 18, IV):</strong> Solicitar a exclusão de dados pessoais, salvo quando há obrigação legal de mantê-los.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Anonimização (art. 18, V):</strong> Solicitar a anonimização de seus dados em vez de sua eliminação.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Portabilidade (art. 18, VI):</strong> Solicitar uma cópia de seus dados em formato estruturado e legível.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Direito de Oposição (art. 18, VII):</strong> Opor-se ao tratamento de dados baseado em interesse legítimo.
            </Typography>
            <Typography variant="body2">
              <strong>Direito de Não Discriminação (art. 20):</strong> Não sofrer discriminação por exercer seus direitos.
            </Typography>
          </Box>
        </Box>

        {/* 10. Como Exercer Seus Direitos */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            10. Como Exercer Seus Direitos?
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Para exercer qualquer dos seus direitos, entre em contato conosco através dos canais abaixo. Você pode fazer uma solicitação de:
          </Typography>

          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • <strong>E-mail:</strong> suporte@agendario.com.br
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • <strong>DPO:</strong> dpo@blasoft.dev
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • <strong>Formulário na Plataforma:</strong> Disponível nas configurações de privacidade
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ lineHeight: 1.8, marginTop: 1.5 }}>
            Responderemos sua solicitação no prazo máximo de <strong>15 dias úteis</strong> conforme exigido pela LGPD (art. 12, § 3º). Solicitações complexas podem estender-se por até 30 dias.
          </Typography>
        </Box>

        {/* 11. Cookies */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            11. Política de Cookies
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            O Agendário utiliza cookies (pequenos arquivos armazenados no seu navegador) para:
          </Typography>

          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Cookies Essenciais:</strong> Necessários para autenticação, segurança e funcionamento básico (exemplo: token de sessão).
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Cookies de Preferência:</strong> Armazenam suas preferências de idioma, tema de cor e outras configurações.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Cookies de Análise:</strong> Ajudam-nos a entender como você usa a plataforma (exemplo: páginas visitadas, tempo de permanência).
            </Typography>
            <Typography variant="body2">
              <strong>Cookies de Marketing:</strong> Nenhum dado é compartilhado com terceiros para fins de publicidade direcionada.
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ lineHeight: 1.8, marginTop: 1.5 }}>
            Você pode controlar cookies através das configurações do seu navegador. Desativar cookies essenciais pode prejudicar a funcionalidade do serviço.
          </Typography>
        </Box>

        {/* 12. Transferência de Dados Internacionais */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            12. Transferência de Dados Internacionais
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Seus dados pessoais podem ser armazenados e processados em servidores localizados no Brasil e, quando necessário, em outros países que mantêm padrões de proteção equivalentes aos da LGPD.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Quando transferirmos dados para fora do Brasil, nós:
          </Typography>
          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Verificamos se o país destino oferece nível de proteção adequado (conforme art. 33, LGPD)
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Assinamos contratos de processamento de dados (Data Processing Agreements)
            </Typography>
            <Typography variant="body2">
              • Aplicamos salvaguardas técnicas como criptografia de ponta a ponta
            </Typography>
          </Box>
        </Box>

        {/* 13. Responsabilidade e Indenização */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            13. Responsabilidade e Indenização
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1.5 }}>
            Conforme a LGPD (art. 42-44), o controlador é responsável pelos danos causados em virtude do tratamento de dados pessoais inadequado. Você pode ter direito a indenização por:
          </Typography>
          <Box sx={{ marginBottom: 1, paddingLeft: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Danos morais decorrentes da violação de direitos relacionados aos dados pessoais
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              • Danos patrimoniais causados por tratamento irregular
            </Typography>
            <Typography variant="body2">
              • Custos de mitigação de danos e recuperação de dados
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ lineHeight: 1.8, marginTop: 1.5 }}>
            O operador compartilha responsabilidade quando é culpado pelos danos causados. Estamos preparados para responder adequadamente a qualquer reclamação de violação de privacidade.
          </Typography>
        </Box>

        {/* 14. Alterações nesta Política */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            14. Alterações nesta Política
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
            Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas, tecnologia ou legislação aplicável. Atualizações relevantes serão comunicadas por e-mail ou através de notificação em nossa plataforma. Seu uso continuado da plataforma após as alterações constitui sua aceitação.
          </Typography>
        </Box>

        {/* 15. Contato */}
        <Box sx={{ marginBottom: 3, paddingTop: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ marginBottom: 1.5, fontWeight: 600 }}>
            15. Entre em Contato Conosco
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, marginBottom: 1 }}>
            Caso tenha dúvidas, sugestões ou deseje exercer seus direitos previstos na LGPD:
          </Typography>

          <Box sx={{ paddingLeft: 2, paddingTop: 1 }}>
            {/* Empresa */}
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>Empresa:</strong> Blasoft Desenvolvimento de Software Ltda.
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>CNPJ:</strong> <span style={{ fontFamily: 'monospace' }}>37.492.558/0001-41</span>
            </Typography>

            {/* DPO - Encarregado de Proteção de Dados */}
            <Typography variant="body2" sx={{ marginBottom: 1.2 }}>
              <strong>Encarregado de Proteção de Dados (DPO):</strong> André Mansur Gonçalves
              <br />
              {/* Email obfuscado contra crawlers */}
              <span style={{ fontSize: '0.85rem', color: '#666' }}>
                Contato:{' '}
                <a 
                  href="mailto:dpo@blasoft.dev"
                  style={{ 
                    color: '#1976d2', 
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                  title="Clique para enviar email ao DPO"
                >
                  dpo [arroba] blasoft [ponto] dev
                </a>
              </span>
            </Typography>

            {/* Suporte Geral */}
            <Typography variant="body2" sx={{ marginBottom: 0.8 }}>
              <strong>E-mail de Suporte:</strong> suporte@agendario.com.br
            </Typography>

            {/* Telefone - Protegido contra crawlers */}
            <Typography 
              variant="body2" 
              sx={{ marginBottom: 0.8 }}
              data-phone-dpo="true"
              data-obfuscated="true"
            >
              <strong>Telefone (DPO):</strong>{' '}
              <span style={{ fontFamily: 'monospace', userSelect: 'none' }}>
                +55 (41) 98402-9395
              </span>
            </Typography>

            {/* Endereço Físico */}
            <Typography variant="body2" sx={{ marginBottom: 1.2 }}>
              <strong>Endereço para Correspondência:</strong>
              <br />
              <span style={{ fontSize: '0.9rem', color: '#555', marginLeft: '1rem' }}>
                Rua Renato Thadeo, 77<br />
                Curitiba, PR - Brasil
              </span>
            </Typography>

            {/* Horário de Atendimento */}
            <Typography variant="body2">
              <strong>Horário de Atendimento:</strong> Segunda a sexta, 9h às 18h (Horário de Brasília)
            </Typography>
          </Box>

          {/* Nota sobre LGPD */}
          <Box sx={{ marginTop: 2.5, paddingTop: 2, borderTop: '1px solid #f0f0f0', backgroundColor: '#f9f9f9', padding: 1.5, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>
              <strong>Observação:</strong> Conforme a LGPD (Lei 13.709/2018), você possui direito de contato com o Encarregado de Proteção de Dados para questões relacionadas ao tratamento de seus dados pessoais. Recomendamos utilizar este canal para solicitações de acesso, retificação ou exclusão de dados.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ marginTop: 4, paddingTop: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            Versão 1.1 | Janeiro 2026 | Este documento foi elaborado de acordo com a Lei Geral de Proteção de Dados Pessoais (LGPD - Lei nº 13.709/2018).
            <br />
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}
